import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Copy, Download, Code2, FileDiff, Check } from 'lucide-react';
import { usePlotlyCode, UsePlotlyCodeResult } from '@/hooks/use-plotly-code';

interface CodeTabProps {
  codeHook?: UsePlotlyCodeResult;
}

export default function CodeTab({ codeHook }: CodeTabProps) {
  // Use provided hook result if available, otherwise call hook internally (fallback for edge cases)
  const hookResult = codeHook ?? usePlotlyCode('python');
  const { language, setLanguage, code, copyToClipboard, download, downloadEdited } = hookResult;
  const [copied, setCopied] = useState(false);
  const copiedTimeoutRef = useRef<number | null>(null);

  return (
    <div className="h-full flex flex-col">
      <Card className="h-full flex flex-col">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Code2 className="h-4 w-4" />
              Code
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                data-state={copied ? 'copied' : 'idle'}
                onClick={async () => {
                  await copyToClipboard();
                  setCopied(true);
                  if (copiedTimeoutRef.current) window.clearTimeout(copiedTimeoutRef.current);
                  // @ts-ignore setTimeout returns number in browsers
                  copiedTimeoutRef.current = window.setTimeout(() => setCopied(false), 1500);
                }}
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4 mr-1 text-emerald-500" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-1" />
                    Copy
                  </>
                )}
                <span className="sr-only" aria-live="polite">{copied ? 'Copied to clipboard' : ''}</span>
              </Button>
              <Button variant="outline" size="sm" onClick={download}>
                <Download className="h-4 w-4 mr-1" />
                Download
              </Button>
              <div className="relative">
                <Button variant="secondary" size="sm" onClick={downloadEdited} title="Download CodeGen script with editor patches">
                  <FileDiff className="h-4 w-4 mr-1" />
                  Download Edited Python
                </Button>
                <Badge className="absolute -top-1 -right-1 px-1.5 py-0.5 text-[10px] pointer-events-none shadow-sm" variant="secondary">
                  Soon
                </Badge>
              </div>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-1 overflow-hidden">
          <Tabs value={language} onValueChange={(v) => setLanguage(v as any)} className="h-full flex flex-col">
            <TabsList className="w-fit">
              <TabsTrigger value="python">Python</TabsTrigger>
              <TabsTrigger value="javascript">JavaScript</TabsTrigger>
            </TabsList>
            <TabsContent value="python" className="flex-1 overflow-hidden">
              <ScrollArea className="h-full">
                <pre className="code-syntax text-sm whitespace-pre-wrap" data-testid="code-python">
{code}
                </pre>
              </ScrollArea>
            </TabsContent>
            <TabsContent value="javascript" className="flex-1 overflow-hidden">
              <ScrollArea className="h-full">
                <pre className="code-syntax text-sm whitespace-pre-wrap" data-testid="code-javascript">
{code}
                </pre>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}


